import type { Folder } from '@/api/types';

/**
 * Where the Scheduled and Snoozed views live (ADR 0076 §17).
 *
 * Static segments like the Outbox's (`features/outbox/route.ts`), so
 * react-router ranks them above `mail/:folderId`. A real folder id can never
 * collide: folder ids on the wire are opaque base64url (ADR 0012), never a
 * bare lowercase word. The folder rows whose role is `scheduled` / `snoozed`
 * link here instead of to the plain folder list, because the job — when it
 * goes, why it failed, how to stop it — is what the person needs to see, and
 * the plain list of the IMAP folder cannot say it.
 */

export const SCHEDULED_SEGMENT = 'scheduled';
export const SNOOZED_SEGMENT = 'snoozed';
export const SCHEDULED_PATH = `/mail/${SCHEDULED_SEGMENT}`;
export const SNOOZED_PATH = `/mail/${SNOOZED_SEGMENT}`;

export const SCHEDULED_LABEL = 'Scheduled';
export const SNOOZED_LABEL = 'Snoozed';

export function isScheduledPath(pathname: string): boolean {
  return pathname === SCHEDULED_PATH || pathname.startsWith(`${SCHEDULED_PATH}/`);
}

export function isSnoozedPath(pathname: string): boolean {
  return pathname === SNOOZED_PATH || pathname.startsWith(`${SNOOZED_PATH}/`);
}

/** Where a folder row goes: its view for the two job folders, else its own list. */
export function folderPath(folder: Pick<Folder, 'id' | 'role'>): string {
  if (folder.role === 'scheduled') return SCHEDULED_PATH;
  if (folder.role === 'snoozed') return SNOOZED_PATH;
  return `/mail/${encodeURIComponent(folder.id)}`;
}

/** The segment `selectedFolderId` takes while one of the views is open, for that folder's row. */
export function viewSegmentOf(folder: Pick<Folder, 'role'>): string | undefined {
  if (folder.role === 'scheduled') return SCHEDULED_SEGMENT;
  if (folder.role === 'snoozed') return SNOOZED_SEGMENT;
  return undefined;
}
